import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import axiosInstance from "../../Services/AxiosAPICaller";
import { IContact } from "../../Interfaces/IContacts";
import PhoneIcon from "./PhoneIconSvg";
import MailIcon from "./MailIconSvg";
import ContactDetails from "../../Pages/ContactDetails/ContactDetails";
import "./ContactGetter.css";

const ContactGetter: React.FC = () => {
  const { Id } = useParams<{ Id: string }>();
  const [selectedContact, setSelectedContact] = useState<IContact | null>(null);
  const [contacts, setContacts] = useState<IContact[]>([]);
  const [siteStartNumber, setSitePhoneStart] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  // Réinitialiser la pagination quand l'ID change
  useEffect(() => {
    window.scrollTo(0, 0);
    setContacts([]);
    setCurrentPage(1);
    setHasMore(true);
  }, [Id]);

  // Fetch des contacts avec pagination
  const fetchContacts = useCallback(
    async (page: number) => {
      if (!Id) return;

      setLoading((prevLoading) => {
        if (prevLoading) return prevLoading; // Déjà en cours de chargement
        return true;
      });

      setError(null);

      try {
        const response = await axiosInstance.get<{
          data: { id: number; attributes: any }[];
          meta: {
            pagination: { page: number; pageSize: number; pageCount: number };
          };
        }>(
          `/contacts?populate[groupes_de_contact][fields][0]=Id&filters[groupes_de_contact][id]=${Id}&pagination[page]=${page}&pagination[pageSize]=10`
        );

        const pagination = response.data.meta.pagination;
        setHasMore(page < pagination.pageCount);

        const newContacts = response.data.data.map((item) => ({
          Id: item.id,
          Name: item.attributes.Nom,
          Mail: item.attributes.Mail,
          Phone: item.attributes.Telephone || null,
          ContactGroup: item.attributes.groupes_de_contact,
          Position: item.attributes.Position,
        }));

        // Fusion et tri des contacts
        setContacts((prevContacts) => {
          // Vérifier les doublons avant d'ajouter
          const existingIds = new Set(prevContacts.map((c) => c.Id));
          const uniqueNewContacts = newContacts.filter(
            (c) => !existingIds.has(c.Id)
          );

          const allContacts = [...prevContacts, ...uniqueNewContacts];

          const contactsWithPosition = allContacts.filter(
            (c) => c.Position !== null
          );
          const contactsWithoutPosition = allContacts.filter(
            (c) => c.Position === null
          );

          contactsWithPosition.sort((a, b) => a.Position - b.Position);
          contactsWithoutPosition.sort((a, b) => a.Name.localeCompare(b.Name));

          return [...contactsWithPosition, ...contactsWithoutPosition];
        });
      } catch (error) {
        setError("Erreur lors de la récupération des contacts");
        console.error("Erreur lors de la récupération des objets :", error);
      } finally {
        setLoading(false);
      }
    },
    [Id]
  ); // Seulement Id pour éviter les re-renders inutiles

  // Fetch du numéro de téléphone du site
  const fetchSitePhone = useCallback(async () => {
    try {
      const response = await axiosInstance.get<{
        data: { id: number; attributes: any }[];
      }>(`/sites`);

      if (Array.isArray(response.data.data)) {
        const siteStartNumber =
          response.data.data[0]?.attributes.PhoneStartNumber || "";
        setSitePhoneStart(siteStartNumber);
      }
    } catch (error) {
      console.error("Erreur lors de la récupération du Site :", error);
    }
  }, []);

  // Chargement initial
  useEffect(() => {
    fetchSitePhone();
  }, [fetchSitePhone]);

  // Chargement des contacts quand currentPage change
  useEffect(() => {
    if (currentPage > 0 && Id) {
      fetchContacts(currentPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, Id]);

  // Écouteur de scroll
  useEffect(() => {
    const handleScroll = () => {
      if (
        window.innerHeight + document.documentElement.scrollTop <
          document.documentElement.offsetHeight - 100 ||
        loading ||
        !hasMore
      ) {
        return;
      }
      setCurrentPage((prev) => prev + 1);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [loading, hasMore]);

  const openDetailsPopup = (contact: IContact) => {
    setSelectedContact(contact);
    document.body.classList.add("ohidden");
  };

  const closeDetailsPopup = () => {
    setSelectedContact(null);
    document.body.classList.remove("ohidden");
  };

  return (
    <div className="ContactContainer">
      {error && <p className="error-message">{error}</p>}
      {contacts.length > 0
        ? contacts.map(
            (C) =>
              (C.Phone || C.Mail) && (
                <div
                  className="ContactTile"
                  key={C.Id}
                  onClick={() => openDetailsPopup(C)}
                >
                  <div className="ContactTileHeader">
                    <p className="ContactName">{C.Name}</p>
                  </div>
                  <div className="ContactInfoDiv">
                    {C.Phone && (
                      <p className="PhoneString">
                        <PhoneIcon />
                        <span>{C.Phone}</span>
                      </p>
                    )}
                    {C.Mail && (
                      <p className="MailString">
                        <MailIcon />
                        <span>{C.Mail}</span>
                      </p>
                    )}
                  </div>
                </div>
              )
          )
        : !loading && <p>Aucuns Contacts à afficher.</p>}
      {loading && <p className="loading-indicator">Chargement...</p>}
      {selectedContact && (
        <div className="popup-overlay">
          <div className="popup-content">
            <button className="close-button" onClick={closeDetailsPopup}>
              {/* SVG reste identique */}
              Retour
            </button>
            <ContactDetails
              Contact={selectedContact}
              SiteStartNumber={siteStartNumber}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactGetter;
